import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Loader,
  SkeletonTableLoading,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  GET_TICKET_LISTVIEW_META,
  AccountClient
} from '@nile/graphql';
import { Workspace } from '@nile/mangifera/src/Containers/PageContainers';
import TaskViewContainer from '@nile/mangifera/src/Containers/TableContainers/TaskViewContainer';
import TicketListContainer from '@nile/mangifera/src/Containers/PageContainers/TicketListContainer';
import TaskListContainer from '@nile/mangifera/src/Containers/PageContainers/TaskListContainer';
import MailContainer from '@nile/mangifera/src/Containers/TableContainers/MailContainer';
import { GET_TASK_LISTVIEW_META } from '@nile/mangifera/src/Graphql/Mutations/ticketview';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { ProjectList } from '@nile/projects/src/Pages';
import { VIEW_ID } from '@nile/client_management/src/Constants/Client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MONICA_AI_VIEWS } from '@nile/mangifera/src/Constants/workspace';
import ProjectListContainer from '@nile/projects/src/Containers/ProjectListContainer';
import { GET_PROJECTS_LISTVIEW_META } from '@nile/projects/src/Graphql';
import { useIsAuthorisedHook } from '@nile/components/src/Utils/helpers';

function SubPage(props) {
  const {
    name,
    viewtype,
    ticketViews,
    taskViews,
    projectViews,
    activeView,
    context
  } = props;
  const flags = useFlags();
  const [viewType, setViewType] = useState(viewtype);
  const [renderContainer, setRenderContainer] = useState(false);
  const setRenderContainerVisibility = (visibility) => {
    setRenderContainer(visibility);
  };
  /**
   * On view switch, instead of resetting in every single component,
   * we handle it in root level.
   * After setting the conditions, we set the state of the component in the callback.
   */

  const isProjectViewAuthorised = useIsAuthorisedHook('project.view');

  const [
    getProjectListViewMeta,
    { loading: projectMetaLoading, data: projectMeta }
  ] = useLazyQuery(GET_PROJECTS_LISTVIEW_META, {
    onError: (error) => {
      console.error('Error fetching project list view meta:', error);
    }
  });

  useEffect(() => {
    if (viewType !== props.viewtype) {
      setViewType(props.viewtype);
    }
    setRenderContainerVisibility(false);
    if (context.viewConditions) {
      context.setViewConditions(
        null,
        setRenderContainerVisibility.bind(this, true)
      );
    } else {
      setRenderContainerVisibility(true);
    }
    if (isProjectViewAuthorised) {
      getProjectListViewMeta();
    }
  }, [props.viewtype, isProjectViewAuthorised, getProjectListViewMeta]);
  const currentView = VIEW_ID;
  const {
    loading: entityPropertiesLoading,
    data: entityProperties,
    error: entityError
  } = useQuery(GET_ENTITY_PROPERTIES, { client: AccountClient });

  const {
    loading: taskMetaLoading,
    error: taskMetaError,
    data: taskMeta
  } = useQuery(GET_TASK_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });
  const {
    loading: ticketListViewMetaLoading,
    error: ticketMetaError,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  if (
    entityPropertiesLoading ||
    taskMetaLoading ||
    ticketListViewMetaLoading ||
    projectMetaLoading
  )
    return <SkeletonTableLoading />;
  if (viewType !== props.viewtype || !renderContainer) return <Loader />;
  switch (viewType) {
    case 'tasks':
      return (
        <TaskListContainer
          allEntities={entityProperties.getEntitiesMeta}
          ticketViews={ticketViews}
          taskViews={taskViews}
          taskViewMeta={taskMeta.getTaskListViewMetaInfo}
        />
      );
    case 'project':
      return (
        <ProjectListContainer
          allEntities={entityProperties.getEntitiesMeta}
          projectViews={projectViews}
          projectViewMeta={projectMeta?.getProjectListViewMetaInfo}
        />
      );
    case 'mails':
      return <MailContainer activeView={activeView} />;
    case 'monica-ai':
      const Component = MONICA_AI_VIEWS.find(
        (view) => view.id === activeView.id
      )?.component;
      return Component ? <Component activeView={activeView} /> : null;
    default:
      return (
        <TicketListContainer
          allEntities={entityProperties.getEntitiesMeta}
          ticketViews={ticketViews}
          ticketViewMeta={ticketListViewMeta.getTicketListViewMetaInfo}
        />
      );
  }
}

export { SubPage };
